// ============================================================
// InvitesPage — MEN-239 [V1-11]
// Invitations niveau instance (mode "sur invitation").
// Génère des codes single/multi-use expirables, les liste, les
// révoque. Toggle "Inscription sur invitation" (InstanceConfig).
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { AdminInvite } from '@siverix/api-client';
import type { AuditLogLive, InstanceConfig } from '@siverix/shared';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

/** [2.10.0] Actions du journal d'audit qui changent cette page. */
const INVITE_ACTIONS = new Set(['invite.create', 'invite.revoke', 'instance.config.update']);

export function InvitesPage(): JSX.Element {
  const [rows, setRows] = useState<AdminInvite[]>([]);
  const [cfg, setCfg] = useState<InstanceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const { socket } = useSocket();

  // [2.10.0] `quiet` : relecture en direct, sans écran « Chargement… » et sans
  // effacer un message d'erreur affiché.
  const fetchAll = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) {
      setLoading(true);
      setErr(null);
    }
    try {
      const [inv, instance] = await Promise.all([api.adminListInvites(), api.adminInstance()]);
      setRows(inv);
      setCfg(instance);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // [2.10.0] Invitation créée ou révoquée, mode « sur invitation » changé par
  // un autre admin : la page se relit d'elle-même (avant : jamais).
  useEffect(() => {
    if (!socket) return;
    const onAudit = (entry: AuditLogLive): void => {
      if (INVITE_ACTIONS.has(entry.action)) void fetchAll(true);
    };
    socket.on('audit:new', onAudit);
    return () => {
      socket.off('audit:new', onAudit);
    };
  }, [socket, fetchAll]);

  async function toggleInvitesOnly(next: boolean): Promise<void> {
    try {
      const updated = await api.adminUpdateInstance({ invitesOnly: next });
      setCfg(updated);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function create(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const payload: { maxUses?: number; expiresAt?: string } = {};
      if (maxUses.trim()) payload.maxUses = Math.max(1, parseInt(maxUses, 10) || 1);
      if (expiresAt) payload.expiresAt = new Date(`${expiresAt}T23:59:59`).toISOString();
      await api.adminCreateInvite(payload);
      setMaxUses('');
      setExpiresAt('');
      await fetchAll();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function revoke(code: string): Promise<void> {
    try {
      await api.adminDeleteInvite(code);
      await fetchAll();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  function copy(code: string): void {
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(code);
        setTimeout(() => setCopied((c) => (c === code ? null : c)), 1500);
      },
      () => undefined,
    );
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Invitations</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        En mode « sur invitation », l'inscription publique est verrouillée : seul un code valide
        permet de créer un compte. Les codes peuvent être à usage unique ou multiple, et expirer.
      </p>

      {err && <ErrorBox message={err} />}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginBottom: '16px',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '12px 14px',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={cfg?.invitesOnly ?? false}
            disabled={!cfg}
            onChange={(e) => void toggleInvitesOnly(e.target.checked)}
          />
          Inscription uniquement sur invitation
        </label>
        <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
          {cfg?.invitesOnly
            ? 'Actif — un code est requis pour /auth/signup.'
            : 'Inactif — inscription publique ouverte.'}
        </span>
      </div>

      <form
        onSubmit={(e) => void create(e)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
          marginBottom: '18px',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '14px',
        }}
      >
        <label style={lbl}>
          Usages max (vide = illimité)
          <input
            type="number"
            min={1}
            placeholder="ex: 1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            style={inp}
          />
        </label>
        <label style={lbl}>
          Expire le (optionnel)
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={inp}
          />
        </label>
        <button type="submit" disabled={submitting} style={btn}>
          {submitting ? 'Génération…' : 'Générer un code'}
        </button>
      </form>

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={th}>Code</th>
                <th style={th}>Usages</th>
                <th style={th}>Expire</th>
                <th style={th}>Créé le</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => {
                const expired = i.expiresAt ? new Date(i.expiresAt).getTime() <= Date.now() : false;
                const exhausted = i.maxUses !== null && i.uses >= i.maxUses;
                return (
                  <tr key={i.code} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td
                      style={{
                        ...td,
                        fontFamily: 'ui-monospace, monospace',
                        fontWeight: 600,
                        opacity: expired || exhausted ? 0.5 : 1,
                      }}
                    >
                      {i.code}
                      {(expired || exhausted) && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 11,
                            color: 'var(--color-error, #f85149)',
                          }}
                        >
                          {expired ? 'expiré' : 'épuisé'}
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {i.uses}
                      {i.maxUses !== null ? ` / ${i.maxUses}` : ' / ∞'}
                    </td>
                    <td style={td}>
                      {i.expiresAt ? new Date(i.expiresAt).toLocaleString('fr-FR') : '—'}
                    </td>
                    <td style={td}>{new Date(i.createdAt).toLocaleString('fr-FR')}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => copy(i.code)} style={btnGhost} type="button">
                        {copied === i.code ? 'Copié ✓' : 'Copier'}
                      </button>{' '}
                      <button onClick={() => void revoke(i.code)} style={btnGhost} type="button">
                        Révoquer
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      ...td,
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      padding: '24px',
                    }}
                  >
                    Aucune invitation.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '8px 16px' };
const lbl: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const inp: React.CSSProperties = {
  padding: '6px 8px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btn: React.CSSProperties = {
  padding: '7px 14px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '5px 10px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};
